import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Users, 
  Calendar, 
  MessageSquare, 
  Rocket,
  Mail,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

export const GetInvolvedPage = () => {
  const ways = [
    {
      icon: Users,
      title: 'Join a Community',
      description: 'Connect with like-minded professionals in tech communities focused on AI, robotics, startups, and more.',
      cta: 'Browse Communities',
      href: '/communities',
    },
    {
      icon: Calendar,
      title: 'Attend Events',
      description: 'Participate in meetups, conferences, hackathons, and workshops happening throughout Portland.',
      cta: 'View Calendar',
      href: '/events',
    },
    {
      icon: MessageSquare,
      title: 'Join Discussions',
      description: 'Share knowledge, ask questions, and connect with the community in our forums.',
      cta: 'Visit Forums',
      href: '/forums',
    },
    {
      icon: Mail,
      title: 'Subscribe',
      description: 'Get weekly updates on Portland tech news, events, and opportunities delivered to your inbox.',
      cta: 'Subscribe Now',
      href: '/newsletter',
    },
  ];

  const opportunities = [
    'Mentor early-stage founders through local accelerators',
    'Speak at meetups and share your expertise',
    'Volunteer at hackathons and tech events',
    'Write for the Portland tech blog',
    'Host office hours for students and career changers',
    'Sponsor community events and programs',
  ];

  return (
    <div data-testid="get-involved-page">
      {/* Header */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <span className="text-label text-muted-foreground mb-4 block">Community</span>
          <h1 className="text-display text-4xl lg:text-5xl mb-4">Get Involved</h1>
          <p className="text-muted-foreground max-w-2xl">
            Portland's tech community is built by people like you. Here's how you 
            can connect, contribute, and grow with Portland's tech community.
          </p>
        </div>
      </section>

      {/* Ways to Get Involved */}
      <section className="border-b border-border">
        <div className="container-wide py-12">
          <div className="grid md:grid-cols-2 gap-6">
            {ways.map((item, index) => (
              <Card key={index} className="card-hover">
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-sm bg-muted flex items-center justify-center mb-6">
                    <item.icon className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <h3 className="font-semibold text-xl mb-3">{item.title}</h3>
                  <p className="text-muted-foreground mb-6">{item.description}</p>
                  <Link to={item.href}>
                    <Button variant="outline">
                      {item.cta}
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Volunteer & Leadership */}
      <section className="border-b border-border bg-muted/30">
        <div className="container-wide py-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-label text-muted-foreground mb-4 block">Volunteer</span>
              <h2 className="text-heading text-3xl mb-4">Give Back to the Community</h2>
              <p className="text-muted-foreground mb-8">
                There are many ways to contribute your time and expertise to help 
                Portland's tech ecosystem thrive.
              </p>
              <ul className="space-y-3">
                {opportunities.map((opp, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-muted-foreground mt-0.5" />
                    <span className="text-sm">{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
            <Card className="card-hover invert-hover">
              <CardContent className="p-8">
                <Rocket className="w-12 h-12 mb-6" />
                <h3 className="font-semibold text-2xl mb-3">Start a Community</h3>
                <p className="text-muted-foreground mb-6">
                  Don't see a community for your niche? We support new community 
                  organizers with resources, promotion, and connections.
                </p>
                <Link to="/contact">
                  <Button variant="outline">
                    Get Started
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section>
        <div className="container-wide py-16">
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="text-heading text-3xl mb-4">Ready to Connect?</h2>
            <p className="text-muted-foreground mb-8">
              Have questions or ideas? We'd love to hear from you.
            </p>
            <Link to="/contact">
              <Button size="lg">
                Contact Us
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
